import React, { useEffect, useState } from 'react';
import api from '../../api';
import AdminSectionHeader from '../../components/admin/AdminSectionHeader';
import ConfirmationModal from '../../components/admin/ConfirmationModal';
import StatusMessage from '../../components/admin/StatusMessage';
import { formatHtmlForEditing } from './emailTemplateUtils';

const SUBJECT_SUGGESTIONS = [
  'Announcement',
  'News',
  'Update',
  'Event Reminder',
  'General Notice',
  'Volunteer Update',
  'Program Update',
];

const parseAddressList = (value = '') => {
  if (!value) return [];
  const entries = value.split(/[\n,;]+/).map((item) => item.trim()).filter(Boolean);
  const uniqueEntries = [...new Set(entries)];
  return uniqueEntries.filter((entry) => entry.includes('@'));
};

const looksLikeDistributionList = (email = '') => {
  const normalized = String(email || '').trim().toLowerCase();
  if (!normalized || !normalized.includes('@')) return false;
  const localPart = normalized.split('@')[0];
  const tokens = localPart.split(/[^a-z0-9]+/).filter(Boolean);
  return tokens.some((token) => ['dl', 'dlist', 'list', 'group', 'distribution', 'mailing', 'announce', 'newsletter'].includes(token));
};

const formatHistoryTimestamp = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    return value;
  }
};

export default function EmailPage() {
  const [emailForm, setEmailForm] = useState({
    subject: '',
    body: '',
    recipient_type: 'custom',
    to_recipients: 'info@vedantacentercharlotte.org',
    cc_recipients: '',
    bcc_recipients: '',
    batch_size: 30,
    attachments: [],
  });
  const [emailMsg, setEmailMsg] = useState({ type: '', text: '' });
  const [isSending, setIsSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [distributionLists, setDistributionLists] = useState([]);
  const [fieldPresets, setFieldPresets] = useState({ to: 'default-info', cc: 'freeform', bcc: 'freeform' });
  const [confirmation, setConfirmation] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  useEffect(() => {
    const loadHistory = async () => {
      try {
        const response = await api.get('/api/admin/email/broadcasts');
        setHistory(response.data || []);
      } catch (error) {
        setHistory([]);
      }
    };

    const loadDistributionLists = async () => {
      try {
        const response = await api.get('/api/admin/distribution-lists');
        setDistributionLists(response.data || []);
      } catch (error) {
        setDistributionLists([]);
      }
    };

    const loadTemplates = async () => {
      try {
        const response = await api.get('/api/admin/email/templates');
        setTemplates(response.data || []);
      } catch (error) {
        setTemplates([]);
      }
    };

    loadHistory();
    loadDistributionLists();
    loadTemplates();
  }, []);

  const showMsg = (type, text, duration = 5000) => {
    setEmailMsg({ type, text });
    if (duration > 0) {
      setTimeout(() => setEmailMsg({ type: '', text: '' }), duration);
    }
  };

  const sendEmailRequest = async (confirmationAccepted = true) => {
    const toRecipients = parseAddressList(emailForm.to_recipients);
    const ccRecipients = parseAddressList(emailForm.cc_recipients);
    const bccRecipients = parseAddressList(emailForm.bcc_recipients);
    const recipientType = emailForm.recipient_type || 'custom';

    if (recipientType !== 'custom') {
      if (!emailForm.subject.trim()) {
        showMsg('error', 'Subject is required.');
        return;
      }
      if (!emailForm.body.trim()) {
        showMsg('error', 'Email body is required.');
        return;
      }
    } else if (!toRecipients.length && !ccRecipients.length && !bccRecipients.length) {
      showMsg('error', 'Add at least one recipient in To, Cc, or Bcc.');
      return;
    }

    if (!emailForm.subject.trim()) {
      showMsg('error', 'Subject is required.');
      return;
    }

    if (!emailForm.body.trim()) {
      showMsg('error', 'Email body is required.');
      return;
    }

    setIsSending(true);
    showMsg('info', 'Sending emails...', 0);

    try {
      const form = new FormData();
      form.append('subject', emailForm.subject.trim());
      form.append('body_html', emailForm.body);
      form.append('recipient_type', recipientType);
      if (recipientType === 'custom') {
        form.append('to_recipients', toRecipients.join(','));
        form.append('cc_recipients', ccRecipients.join(','));
        form.append('bcc_recipients', bccRecipients.join(','));
      } else {
        form.append('to_recipients', '');
        form.append('cc_recipients', '');
        form.append('bcc_recipients', '');
      }
      form.append('batch_size', String(emailForm.batch_size));
      form.append('confirmation_accepted', String(Boolean(confirmationAccepted)));

      emailForm.attachments.forEach((file) => {
        form.append('attachments', file);
      });

      const response = await api.post('/api/admin/email/send', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      showMsg('success', response.data?.message || 'Emails sent successfully');
      setConfirmation(null);
      const historyResponse = await api.get('/api/admin/email/broadcasts');
      setHistory(historyResponse.data || []);
    } catch (err) {
      showMsg('error', `Failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setIsSending(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();

    const toRecipients = parseAddressList(emailForm.to_recipients);
    const ccRecipients = parseAddressList(emailForm.cc_recipients);
    const dlWarnings = [...toRecipients, ...ccRecipients].filter(looksLikeDistributionList);

    if (dlWarnings.length > 0) {
      setConfirmation({
        title: 'Privacy warning for distribution lists',
        message: 'You are sending to a distribution list in To or Cc. Email addresses may become visible to recipients. For privacy, use Bcc when sending to DLs. Do you want to continue?',
        onConfirm: () => sendEmailRequest(true),
      });
      return;
    }

    await sendEmailRequest(true);
  };

  const handlePreview = () => {
    if (!emailForm.subject.trim()) {
      showMsg('error', 'Subject is required.');
      return;
    }

    if (!emailForm.body.trim()) {
      showMsg('error', 'Email body is required.');
      return;
    }

    setIsPreviewOpen(true);
  };

  const previewRecipients = [
    ['To', emailForm.to_recipients],
    ['Cc', emailForm.cc_recipients],
    ['Bcc', emailForm.bcc_recipients],
  ].filter(([, value]) => value.trim());

  const deleteHistoryItem = async (item) => {
    const broadcastId = item.id || item._id;
    if (!broadcastId) return;

    try {
      await api.delete(`/api/admin/email/broadcasts/${broadcastId}`);
      setHistory((current) => current.filter((entry) => (entry.id || entry._id) !== broadcastId));
      showMsg('success', 'Email history record deleted.');
    } catch (error) {
      showMsg('error', `Failed to delete email history: ${error.response?.data?.detail || error.message}`);
    } finally {
      setConfirmation(null);
    }
  };

  const confirmDeleteHistoryItem = (item) => {
    setConfirmation({
      title: 'Delete sent email record?',
      message: `This will remove the history record for “${item.subject || 'Untitled email'}”. It will not undo delivery. Continue?`,
      onConfirm: () => deleteHistoryItem(item),
    });
  };

  const resolveListMembers = async (listId) => {
    try {
      const response = await api.get(`/api/admin/distribution-lists/${listId}/members`, {
        params: { limit: 200 },
      });
      const emails = (response.data || []).map((member) => member.email).filter(Boolean);
      return [...new Set(emails)].join(',\n');
    } catch (error) {
      return '';
    }
  };

  const handleFieldPresetChange = async (fieldName, presetValue) => {
    const presetFieldName = fieldName.replace('_recipients', '');
    setFieldPresets((prev) => ({ ...prev, [presetFieldName]: presetValue }));

    if (fieldName === 'to_recipients' && ['myself', 'admins'].includes(presetValue)) {
      setEmailForm((prev) => ({
        ...prev,
        recipient_type: presetValue,
        to_recipients: '',
      }));
      return;
    }

    setEmailForm((prev) => ({ ...prev, recipient_type: 'custom' }));

    if (presetValue === 'freeform') {
      return;
    }

    if (presetValue === 'default-info') {
      setEmailForm((prev) => ({ ...prev, [fieldName]: 'info@vedantacentercharlotte.org' }));
      return;
    }

    if (!presetValue) {
      return;
    }

    const selectedList = distributionLists.find((list) => list.id === presetValue);
    if (!selectedList) return;

    const memberEmails = await resolveListMembers(selectedList.id);
    const currentValue = (emailForm[fieldName] || '').trim();
    const mergedValue = currentValue ? `${currentValue}\n${memberEmails}`.trim() : memberEmails;
    setEmailForm((prev) => ({ ...prev, [fieldName]: mergedValue }));
  };

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplateId(templateId);
    const selectedTemplate = templates.find((template) => template.id === templateId);
    if (!selectedTemplate) {
      setEmailForm((prev) => ({ ...prev, subject: '', body: '' }));
      return;
    }
    setEmailForm((prev) => ({
      ...prev,
      subject: selectedTemplate.subject,
      body: formatHtmlForEditing(selectedTemplate.body_html),
    }));
  };

  const clearForm = () => {
    setSelectedTemplateId('');
    setEmailForm({
      subject: '',
      body: '',
      recipient_type: 'custom',
      to_recipients: 'info@vedantacentercharlotte.org',
      cc_recipients: '',
      bcc_recipients: '',
      batch_size: 30,
      attachments: [],
    });
    setFieldPresets({ to: 'default-info', cc: 'freeform', bcc: 'freeform' });
  };

  return (
    <div className="admin-email-page">
      <AdminSectionHeader
        title="Send Emails"
        subtitle="Compose and send batched emails to recipients"
      />

      <section className="admin-section">
        <h3>Email Tips</h3>
        <ul className="info-list">
          <li><strong>Default To:</strong> info@vedantacentercharlotte.org</li>
          <li><strong>DL privacy:</strong> prefer Bcc when sending to a distribution list</li>
          <li><strong>Batch limit:</strong> each send batch stays at or under 45 recipients for a safer Azure ceiling</li>
          <li><strong>Body format:</strong> Use Email Template. HTML is supported. Use plain text if you prefer simplicity</li>
          <li><strong>Attachments:</strong> Up to 5 files (10MB max each). Supported: images, PDF, Word docs, CSV, Excel, text files</li>
        </ul>
      </section>

      {emailMsg.text && <StatusMessage type={emailMsg.type} message={emailMsg.text} />}

      <section className="admin-section">
        <form onSubmit={handleEmailSubmit} className="auth-form">
          <label>
            Use Email Template (Recommended)
            <select
              value={selectedTemplateId}
              onChange={(e) => handleTemplateSelect(e.target.value)}
              disabled={isSending}
              className="email-template-select"
            >
              <option value="">Start with a blank email</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>{template.name}</option>
              ))}
            </select>
            <small className="field-helper">Create and edit reusable templates under Manage Email Templates.</small>
          </label>
          <label>
            Subject
            <input
              type="text"
              list="email-subject-suggestions"
              value={emailForm.subject}
              onChange={(e) => setEmailForm((f) => ({ ...f, subject: e.target.value }))}
              placeholder="Choose or type a subject"
              required
              disabled={isSending}
            />
            <datalist id="email-subject-suggestions">
              {SUBJECT_SUGGESTIONS.map((option) => (
                <option key={option} value={option} />
              ))}
            </datalist>
          </label>

          <div className="email-recipient-grid">
            <label>
              To
              <select
                value={fieldPresets.to}
                onChange={(e) => handleFieldPresetChange('to_recipients', e.target.value)}
                disabled={isSending}
                className="email-recipient-select"
              >
                <option value="freeform">Freeform</option>
                <option value="myself">Myself (logged-in admin)</option>
                <option value="admins">Admins Only</option>
                <option value="default-info">Default: info@vedantacentercharlotte.org</option>
                {distributionLists.map((list) => (
                  <option key={list.id} value={list.id}>{list.name}</option>
                ))}
              </select>
              <small className="field-helper">Add a DL here or type addresses manually.</small>
              <textarea
                value={emailForm.to_recipients}
                onChange={(e) => setEmailForm((f) => ({ ...f, to_recipients: e.target.value }))}
                rows={3}
                placeholder="Enter one or more addresses separated by commas, semicolons, or new lines"
                disabled={isSending}
              />
            </label>

            <label>
              Cc
              <select
                value={fieldPresets.cc}
                onChange={(e) => handleFieldPresetChange('cc_recipients', e.target.value)}
                disabled={isSending}
                className="email-recipient-select"
              >
                <option value="freeform">Freeform</option>
                {distributionLists.map((list) => (
                  <option key={list.id} value={list.id}>{list.name}</option>
                ))}
              </select>
              <small className="field-helper">Use only when recipients should be visible.</small>
              <textarea
                value={emailForm.cc_recipients}
                onChange={(e) => setEmailForm((f) => ({ ...f, cc_recipients: e.target.value }))}
                rows={3}
                placeholder="Optional"
                disabled={isSending}
              />
            </label>

            <label>
              Bcc
              <select
                value={fieldPresets.bcc}
                onChange={(e) => handleFieldPresetChange('bcc_recipients', e.target.value)}
                disabled={isSending}
                className="email-recipient-select"
              >
                <option value="freeform">Freeform</option>
                {distributionLists.map((list) => (
                  <option key={list.id} value={list.id}>{list.name}</option>
                ))}
              </select>
              <small className="field-helper">Recommended for DLs to keep addresses private.</small>
              <textarea
                value={emailForm.bcc_recipients}
                onChange={(e) => setEmailForm((f) => ({ ...f, bcc_recipients: e.target.value }))}
                rows={3}
                placeholder="Recommended for DLs"
                disabled={isSending}
              />
            </label>
          </div>

          <div className="email-tip-box">
            <strong>Privacy recommendation:</strong> use Bcc for distribution lists so recipient email addresses stay private.
          </div>

          <label>
            Batch Size (recipients per send batch)
            <input
              type="number"
              value={emailForm.batch_size}
              min="1"
              max="45"
              onChange={(e) => setEmailForm((f) => ({ ...f, batch_size: parseInt(e.target.value, 10) || 30 }))}
              disabled={isSending}
            />
            <small style={{ display: 'block', marginTop: '0.2rem' }}>
              Recommended: 30. Azure ceiling is 50; the system uses a safer 45-recipient maximum.
            </small>
          </label>

          <label>
            Email Body
            <textarea
              value={emailForm.body}
              onChange={(e) => setEmailForm((f) => ({ ...f, body: e.target.value }))}
              placeholder="Enter HTML or plain text email body..."
              className="email-body-editor"
              rows={12}
              required
              disabled={isSending}
            />
          </label>

          <label className="email-attachment-label">
            Attachments
            <div className="email-attachment-input-wrapper">
              <input
                type="file"
                id="email-attachments"
                multiple
                accept="image/*,.pdf,.doc,.docx,.txt,.csv,.xls,.xlsx"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  if (emailForm.attachments.length + files.length > 5) {
                    alert('Maximum 5 attachments allowed');
                    return;
                  }
                  setEmailForm((f) => ({ ...f, attachments: [...f.attachments, ...files] }));
                  e.target.value = '';
                }}
                disabled={isSending}
              />
              <label htmlFor="email-attachments" className="email-attachment-btn">
                <i className="fa-solid fa-paperclip"></i> Choose Files
              </label>
              <span className="email-attachment-hint">
                Max 5 files, 10MB each. Supported: images, PDF, Word docs, CSV, Excel, text.
              </span>
            </div>
            {emailForm.attachments.length > 0 && (
              <ul className="email-attachment-list">
                {emailForm.attachments.map((file, index) => (
                  <li key={index} className="email-attachment-item">
                    <i className="fa-solid fa-file"></i>
                    <span className="email-attachment-name">{file.name}</span>
                    <span className="email-attachment-size">
                      ({(file.size / 1024 / 1024).toFixed(2)} MB)
                    </span>
                    <button
                      type="button"
                      className="email-attachment-remove"
                      onClick={() =>
                        setEmailForm((f) => ({
                          ...f,
                          attachments: f.attachments.filter((_, i) => i !== index),
                        }))
                      }
                      disabled={isSending}
                      title="Remove attachment"
                    >
                      <i className="fa-solid fa-times"></i>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </label>

          <div className="email-form-actions">
            <button type="button" className="btn btn-secondary" onClick={handlePreview} disabled={isSending}>
              <i className="fa-solid fa-eye"></i> Preview
            </button>
            <button type="submit" disabled={isSending}>
              {isSending ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin"></i> Sending...
                </>
              ) : (
                <>
                  <i className="fa-solid fa-paper-plane"></i> Send Email
                </>
              )}
            </button>
            <button type="button" className="btn btn-secondary" onClick={clearForm} disabled={isSending}>
              Clear
            </button>
          </div>
        </form>
      </section>

      <section className="admin-section">
        <h3>Recent Sent Emails</h3>
        {history.length > 0 ? (
          <div className="email-history-list">
            {history.map((item) => (
              <div key={item.id || item._id || item.subject + item.created_at} className="email-history-item">
                <div className="email-history-main">
                  <strong>{item.subject || 'Untitled email'}</strong>
                  <span>{formatHistoryTimestamp(item.created_at)}</span>
                </div>
                <div className="email-history-meta">
                  <span>{item.recipient_count || 0} recipients</span>
                  <div className="email-history-actions">
                    <span className={`email-history-status status-${String(item.status || 'unknown').toLowerCase()}`}>
                      {item.status || 'unknown'}
                    </span>
                    <button
                      type="button"
                      className="btn btn-danger email-history-delete"
                      onClick={() => confirmDeleteHistoryItem(item)}
                      title="Delete sent email record"
                      aria-label={`Delete ${item.subject || 'untitled email'} history record`}
                    >
                      <i className="fa-solid fa-trash"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="empty-state">No sent email history yet.</p>
        )}
      </section>


      <ConfirmationModal
        isOpen={Boolean(confirmation)}
        title={confirmation?.title || 'Confirm'}
        message={confirmation?.message || ''}
        onConfirm={() => {
          if (confirmation?.onConfirm) confirmation.onConfirm();
        }}
        onCancel={() => setConfirmation(null)}
        variant="warning"
      />

      {isPreviewOpen && (
        <div
          className="modal-overlay email-preview-overlay"
          onClick={() => setIsPreviewOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="email-preview-title"
        >
          <div className="modal-content email-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 id="email-preview-title">Email Preview</h3>
              <button
                type="button"
                className="modal-close"
                onClick={() => setIsPreviewOpen(false)}
                aria-label="Close email preview"
              >
                &times;
              </button>
            </div>
            <div className="modal-body email-preview-body">
              <div className="email-preview-details">
                <strong>{emailForm.subject}</strong>
                {previewRecipients.map(([label, recipients]) => (
                  <div key={label}>
                    <span>{label}:</span> {parseAddressList(recipients).join(', ')}
                  </div>
                ))}
              </div>
              <iframe
                title="Rendered email preview"
                className="email-preview-frame"
                sandbox=""
                srcDoc={emailForm.body}
              />
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setIsPreviewOpen(false)}>
                Close
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => {
                  setIsPreviewOpen(false);
                  sendEmailRequest(true);
                }}
                disabled={isSending}
              >
                <i className="fa-solid fa-paper-plane"></i> Send Email
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}